import React from "react";
import { injectIntl } from "react-intl";
import { Checkbox, Spin, Pagination, Tooltip } from "antd";
import moment from "moment";
import RightsStateTag from "./RightsStateTag";
import "./RightsManager.css";

/**
 * Vista de lista del Gestor.
 *
 * El árbol responde "cómo está esta carpeta"; la lista responde "qué hay que
 * atender en toda la cuenta", que es lo que no se puede sacar recorriendo
 * carpeta por carpeta. Se apoya en GET /rights/report, que ya devolvía filas
 * paginadas y ordenables aunque hasta ahora sólo se usara para los KPI.
 *
 * Las columnas ordenables son exactamente las que el backend sabe ordenar
 * (RIGHTS_REPORT_SORTS): nombre, fecha de subida, vencimiento y estado. Ofrecer
 * un orden que el servidor ignora daría una tabla que no cambia al hacer clic.
 */
const SORTABLE = {
  rights_manager_col_name: "name",
  rights_manager_col_uploaded_at: "createdAt",
  rights_manager_col_due_date: "dueDate",
  rights_manager_col_state: "rightsState"
};

/** Igual que en el árbol: la extensión del backend manda sobre el nombre. */
function fileLabel(row) {
  if (row && row.extension) return String(row.extension).toUpperCase();
  const parts = ((row && row.name) || "").split(".");
  return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
}

function formatDate(value) {
  return value ? moment(value).format("D/M/YYYY") : "—";
}

const RightsManagerList = ({
  rows = [],
  total = 0,
  page,
  limit,
  orderBy,
  loading,
  selected = new Map(),
  onToggleSelect,
  onChangeOrder,
  onChangePage,
  onOpenDetail,
  intl
}) => {
  const header = (labelId, extra = {}) => {
    const campo = SORTABLE[labelId];
    const activo = campo && (orderBy === campo || orderBy === `-${campo}`);
    const desc = orderBy === `-${campo}`;
    return (
      <th
        style={{
          textAlign: "left",
          padding: "12px 14px",
          cursor: campo ? "pointer" : "default",
          ...extra
        }}
        onClick={() => {
          if (!campo) return;
          // Primer clic: ascendente. Segundo sobre la misma columna: invierte.
          onChangeOrder(activo && !desc ? `-${campo}` : campo);
        }}
      >
        {intl.formatMessage({ id: labelId })}
        {activo && <span className="rm-sort">{desc ? " ↓" : " ↑"}</span>}
      </th>
    );
  };

  return (
    <div>
      <div
        className="rm-tbl"
        style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 4, overflow: "auto" }}
      >
        <table className="rm-table rm-list" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#fafafa" }}>
              <th style={{ width: 44 }} />
              {header("rights_manager_col_name")}
              {header("rights_manager_col_state")}
              {header("rights_manager_col_due_date")}
              {header("rights_manager_col_uploaded_by")}
              {header("rights_manager_col_uploaded_at")}
              {header("rights_manager_col_assigned_by")}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: "center" }}>
                  <Spin />
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: "center", color: "rgba(0,0,0,.45)" }}>
                  {intl.formatMessage({ id: "rights_manager_empty" })}
                </td>
              </tr>
            )}

            {!loading &&
              rows.map(row => (
                <tr key={row._id} className="rm-row">
                  <td style={{ width: 44, padding: "10px 14px" }}>
                    <Checkbox
                      checked={selected.has(`file:${row._id}`)}
                      onChange={() => onToggleSelect("file", row)}
                    />
                  </td>
                  <td
                    style={{ padding: "10px 14px", cursor: "pointer" }}
                    onClick={() => onOpenDetail(row)}
                  >
                    <span className="rm-file-icon">{fileLabel(row)}</span>
                    <span className="rm-node-name">{row.name}</span>
                    {row.folderPathText && (
                      <Tooltip title={row.folderPathText}>
                        <div className="rm-row-path">{row.folderPathText}</div>
                      </Tooltip>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <RightsStateTag state={row.rightsState} />
                  </td>
                  <td style={{ padding: "10px 14px" }}>{formatDate(row.dueDate)}</td>
                  <td style={{ padding: "10px 14px" }}>{row.username || "—"}</td>
                  <td style={{ padding: "10px 14px" }}>{formatDate(row.createdAt)}</td>
                  <td style={{ padding: "10px 14px" }}>{row.rightsAssignedByName || "—"}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className="rm-pagination">
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            onChange={onChangePage}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default injectIntl(RightsManagerList);
